<template>
    <div class="space-y-2">
        <Label class="gap-1 flex items-center" for="coupon-code">
            {{ $t("billing.coupon.label") }}
        </Label>
        <div class="gap-2 flex">
            <Input
                id="coupon-code"
                v-model="couponCode"
                type="text"
                :placeholder="$t('billing.coupon.placeholder')"
                :disabled="loading || applied"
                class="flex-1"
                @input="onInput"
                @keyup.enter="validateCoupon"
            />
            <Button
                v-if="!applied"
                @click="validateCoupon"
                :loading="loading"
                :disabled="!couponCode.trim() || loading"
                variant="outline"
            >
                {{ $t("billing.coupon.apply") }}
            </Button>
            <Button v-else @click="removeCoupon" variant="outline" size="sm">
                {{ $t("billing.coupon.remove") }}
            </Button>
        </div>

        <!-- Success State -->
        <div
            v-if="applied && validationResult"
            class="rounded-lg border-green-200 bg-green-50 p-3 border"
        >
            <div class="gap-2 flex items-start">
                <CheckCircle class="mt-0.5 text-green-600" :size="16" />
                <div class="flex-1">
                    <p class="text-sm font-medium text-green-800">
                        {{ $t("billing.coupon.applied_success") }}
                    </p>
                    <p class="mt-1 text-sm text-green-700">
                        <span class="font-medium">{{
                            validationResult.coupon.name
                        }}</span>
                        <span v-if="validationResult.coupon.description">
                            - {{ validationResult.coupon.description }}
                        </span>
                    </p>
                    <div
                        class="mt-2 gap-4 text-sm text-green-700 flex items-center"
                    >
                        <span
                            >{{ $t("billing.coupon.discount") }}:
                            {{ validationResult.coupon.formatted_value }}</span
                        >
                        <span
                            >{{ $t("billing.coupon.savings") }}:
                            {{
                                formatPrice(
                                    validationResult.pricing.discount_amount,
                                    validationResult.pricing.currency,
                                )
                            }}</span
                        >
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="rounded-lg border-red-200 bg-red-50 p-3 border"
        >
            <div class="gap-2 flex items-start">
                <XCircle class="mt-0.5 text-red-600" :size="16" />
                <div class="flex-1">
                    <p class="text-sm font-medium text-red-800">
                        {{ $t("billing.coupon.error") }}
                    </p>
                    <p class="mt-1 text-sm text-red-700">
                        {{ error }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, XCircle } from "lucide-vue-next";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { formatPriceCurrency } from "../utils/formatPriceCurrency";

interface CouponValidationResult {
    valid: boolean;
    coupon: {
        code: string;
        name: string;
        description?: string;
        formatted_value: string;
    };
    pricing: {
        original_amount: number;
        discount_amount: number;
        final_amount: number;
        currency: string;
    };
}

interface Props {
    amount: number;
    currency?: string;
    externalResult?: CouponValidationResult | null;
}

const props = withDefaults(defineProps<Props>(), {
    currency: "CZK",
    externalResult: null,
});

const emit = defineEmits<{
    applied: [code: string, result: CouponValidationResult];
    removed: [];
}>();

const couponCode = ref("");
const loading = ref(false);
const applied = ref(false);
const error = ref<string | null>(null);
const validationResult = ref<CouponValidationResult | null>(null);
let debounceTimer: number | null = null;

const formatPrice = (amount: number, currency: string) => {
    return formatPriceCurrency(amount, currency);
};

// Watch for external result updates (when parent recalculates coupon)
watch(
    () => props.externalResult,
    (newResult) => {
        if (newResult && applied.value) {
            validationResult.value = newResult;
            error.value = null;
        }
    },
    { deep: true },
);

const onInput = () => {
    if (applied.value) return;

    error.value = null;

    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }

    // Auto-validate after user stops typing for 500ms
    debounceTimer = setTimeout(() => {
        if (couponCode.value.trim() && couponCode.value.length >= 3) {
            validateCoupon();
        }
    }, 500);
};

const validateCoupon = async () => {
    if (!couponCode.value.trim() || loading.value || applied.value) return;

    loading.value = true;
    error.value = null;

    try {
        const response = await fetch(route("app.coupons.validate"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN":
                    document
                        .querySelector('meta[name="csrf-token"]')
                        ?.getAttribute("content") || "",
            },
            body: JSON.stringify({
                code: couponCode.value.trim(),
                amount: props.amount,
            }),
        });

        const data = await response.json();

        if (response.ok && data.valid) {
            validationResult.value = data;
            applied.value = true;
            emit("applied", couponCode.value.trim(), data);
        } else {
            error.value = data.message || "Invalid coupon code";
            validationResult.value = null;
        }
    } catch (err) {
        error.value = "Failed to validate coupon. Please try again.";
        console.error("Coupon validation error:", err);
    } finally {
        loading.value = false;
    }
};

const removeCoupon = () => {
    couponCode.value = "";
    applied.value = false;
    error.value = null;
    validationResult.value = null;
    emit("removed");
};

// Clear debounce timer on unmount
import { onUnmounted } from "vue";
onUnmounted(() => {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }
});
</script>
